import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import { RouteErrorBoundary } from "@/components/error/RouteErrorBoundary";
import { OfflinePage } from "@/components/states";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isApiError } from "@/lib/api/errors";
import { globalDefaults } from "@/lib/api/cache-policies";
import type { ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import Index from "./pages/Index";
import Roteiros from "./pages/Roteiros";
import RoteiroDetail from "./pages/RoteiroDetail";
import Decodificar from "./pages/Decodificar";
import Simulador from "./pages/Simulador";
import SimuladorReportPage from "./pages/SimuladorReport";
import Videos from "./pages/Videos";
import CriativoDetail from "./pages/CriativoDetail";
import Consultas from "./pages/Consultas";
import ConsultaDetail from "./pages/ConsultaDetail";
import ConsultasInsights from "./pages/ConsultasInsights";
import Notificacoes from "./pages/Notificacoes";
// import Instagram from "./pages/Instagram"; // hidden: not implemented
import Ranking from "./pages/Ranking";
import Configuracoes from "./pages/Configuracoes";
import Perfil from "./pages/Perfil";
import Onboarding from "./pages/Onboarding";
import Funil from "./pages/Funil";
import Uso from "./pages/Uso";
import Ajuda from "./pages/Ajuda";
import RaioX from "./pages/RaioX";
import IconReference from "./pages/IconReference";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { OnboardingGate } from "@/components/auth/OnboardingGate";
import { PlanGate } from "@/components/auth/PlanGate";
import { LegalAcceptanceGate } from "@/components/auth/LegalAcceptanceGate";
import { LgpdSection } from "@/features/perfil/components/LgpdSection";
import { LegalLinks } from "@/components/shared/LegalLinks";
import DevSwitchUser from "./pages/DevSwitchUser";
import AdminPanel from "./pages/AdminPanel";
import PendingAccess from "./pages/PendingAccess";
import LegalDocument from "./pages/LegalDocument";
import InscricaoBeta from "./pages/InscricaoBeta";

// Spec: docs/architecture/states-pattern.md §4 / §11 Q1 — query errors are
// surfaced via <ErrorState> on each page; the global cache `onError` only
// logs. Mutations toast explicitly at their call sites.
//
// QueryClient defaults: docs/architecture/data-layer-spec.md §3 / §5.
function retryPolicy(failureCount: number, error: unknown): boolean {
  if (!isApiError(error)) return failureCount < 2;
  switch (error.error.kind) {
    case "auth":
    case "validation":
      return false;
    case "network":
      return failureCount < 3;
    case "server":
      return failureCount < 2;
    case "unknown":
    default:
      return failureCount < 1;
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (import.meta.env.DEV) {
        console.warn("[query:error]", query.queryKey, error);
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: globalDefaults.staleTime,
      gcTime: globalDefaults.gcTime,
      retry: retryPolicy,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

const withRouteBoundary = (routeName: string, element: ReactNode) => (
  <RouteErrorBoundary routeName={routeName}>{element}</RouteErrorBoundary>
);

const protectedRoute = (routeName: string, element: ReactNode) => (
  <ProtectedRoute>
    <LegalAcceptanceGate>
    <PlanGate>
      <OnboardingGate>
        <RouteErrorBoundary routeName={routeName}>{element}</RouteErrorBoundary>
      </OnboardingGate>
    </PlanGate>
    </LegalAcceptanceGate>
  </ProtectedRoute>
);

const App = () => (
  <ErrorBoundary boundaryName="app">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner position="top-right" visibleToasts={3} />
        <BrowserRouter>
          <Routes>
            {/* Public auth routes */}
            <Route path="/login" element={withRouteBoundary("login", <Login />)} />
            <Route path="/register" element={withRouteBoundary("register", <Register />)} />
            <Route path="/inscricao-beta" element={withRouteBoundary("inscricao-beta", <InscricaoBeta />)} />
            <Route path="/pending-access" element={withRouteBoundary("pending-access", <PendingAccess />)} />
            <Route
              path="/auth/callback"
              element={withRouteBoundary("auth-callback", <AuthCallback />)}
            />
            {/* OAuth callback — must match redirectUrl in authenticateWithRedirect() */}
            <Route
              path="/sso-callback"
              element={withRouteBoundary("sso-callback", <AuthCallback />)}
            />
            <Route path="/ajuda" element={withRouteBoundary("ajuda", <Ajuda />)} />

            {/* Protected app routes */}
            <Route path="/meus-dados" element={
              <ProtectedRoute>
                <main className="min-h-screen bg-background p-4 sm:p-6">
                  <div className="mx-auto max-w-3xl space-y-6">
                    <Link to="/" className="inline-flex min-h-11 items-center text-primary underline">Voltar ao início</Link>
                    <h1 className="text-2xl font-semibold text-foreground">Privacidade e meus dados</h1>
                    <LgpdSection />
                    <p className="text-sm text-muted-foreground">Outros pedidos ou problemas com exportação/exclusão: <a className="text-primary underline" href="mailto:contato@ber.adv.br?subject=Privacidade%20-%20CDP">contato@ber.adv.br</a>, canal cadastral da RM7 TREINAMENTOS LTDA.</p>
                    <LegalLinks />
                  </div>
                </main>
              </ProtectedRoute>
            } />
            <Route path="/" element={protectedRoute("home", <Index />)} />
            <Route path="/roteiros" element={protectedRoute("roteiros", <Roteiros />)} />
            <Route path="/roteiros/:id" element={protectedRoute("roteiro-detail", <RoteiroDetail />)} />
            <Route path="/decodificar" element={protectedRoute("decodificar", <Decodificar />)} />
            <Route path="/simulador" element={protectedRoute("simulador", <Simulador />)} />
            <Route
              path="/simulador/relatorio/:sessionId"
              element={protectedRoute("simulador-report", <SimuladorReportPage />)}
            />
            <Route path="/criativos" element={protectedRoute("criativos", <Videos />)} />
            <Route path="/criativos/:id" element={protectedRoute("criativo-detail", <CriativoDetail />)} />
            <Route path="/videos" element={<Navigate to="/criativos" replace />} />
            <Route path="/consultas" element={protectedRoute("consultas", <Consultas />)} />
            <Route path="/consultas/insights" element={protectedRoute("consultas-insights", <ConsultasInsights />)} />
            <Route path="/consultas/:id" element={protectedRoute("consultas-detail", <ConsultaDetail />)} />
            {/* <Route path="/instagram" element={protectedRoute("instagram", <Instagram />)} /> hidden: not implemented */}
            <Route path="/ranking" element={protectedRoute("ranking", <Ranking />)} />
            <Route path="/funil" element={protectedRoute("funil", <Funil />)} />
            <Route path="/raio-x" element={protectedRoute("raio-x", <RaioX />)} />
            {import.meta.env.DEV && (
              <Route path="/icons" element={protectedRoute("icons", <IconReference />)} />
            )}
            <Route
              path="/configuracoes"
              element={protectedRoute("configuracoes", <Configuracoes />)}
            />
            <Route path="/perfil" element={protectedRoute("perfil", <Perfil />)} />
            <Route path="/uso" element={protectedRoute("uso", <Uso />)} />
            <Route path="/onboarding" element={protectedRoute("onboarding", <Onboarding />)} />
            <Route path="/notificacoes" element={protectedRoute("notificacoes", <Notificacoes />)} />

            {/* Admin panel — slug intentionally obscure; non-admins see NotFound */}
            <Route path="/hub" element={protectedRoute("hub", <AdminPanel />)} />

            {/* Dev-only routes */}
            {import.meta.env.DEV && (
              <Route
                path="/dev/switch-user"
                element={withRouteBoundary("dev-switch-user", <DevSwitchUser />)}
              />
            )}

            {/* Public utility routes */}
            <Route path="/offline" element={withRouteBoundary("offline", <OfflinePage />)} />
            <Route path="/legal/:slug" element={withRouteBoundary("legal", <LegalDocument />)} />
            <Route path="/termos" element={<Navigate to="/legal/termos" replace />} />
            <Route path="/privacidade" element={<Navigate to="/legal/privacidade" replace />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={withRouteBoundary("not-found", <NotFound />)} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
